import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_MARK } from "@/lib/api";
import { useSite, bookingUrl } from "@/context/SiteContext";

const LINKS = [
  { href: "#story", label: "Story" },
  { href: "#rooms", label: "Stay" },
  { href: "#journey", label: "Journey" },
  { href: "#gallery", label: "Gallery" },
  { href: "#journal", label: "Journal" },
  { href: "#contact", label: "Contact" },
];

export const Header = () => {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        dark
          ? "backdrop-blur-xl bg-[#F7F5F0]/92 border-b border-ink/10 shadow-[0_8px_30px_rgba(14,27,46,0.08)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={LOGO_MARK} alt="Mahadinusa Lembongan logo" className="h-10 w-10 object-contain" />
          <span className="leading-none">
            <span className={`block font-serif text-lg sm:text-xl tracking-[0.12em] transition-colors ${dark ? "text-ink" : "text-white"}`}>
              MAHADINUSA
            </span>
            <span className={`block text-[10px] font-mono uppercase tracking-[0.42em] transition-colors ${dark ? "text-azure" : "text-white/70"}`}>
              Lembongan
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm transition-colors ${dark ? "text-ink/70 hover:text-ink" : "text-white/80 hover:text-white"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={bookingUrl(settings)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reserve-now-header-button"
            className={`hidden sm:inline-flex items-center rounded-full text-sm px-5 py-2.5 transition-all ${
              dark
                ? "bg-ink text-sand hover:bg-azure hover:shadow-[0_0_0_4px_rgba(26,98,137,0.15)]"
                : "bg-white/15 text-white border border-white/40 backdrop-blur hover:bg-white hover:text-ink"
            }`}
          >
            Reserve Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden h-10 w-10 grid place-items-center rounded-full border transition-colors ${dark ? "border-ink/15 text-ink" : "border-white/40 text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F7F5F0] border-t border-ink/10 px-6 py-5 space-y-4" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block text-base text-ink/80"
            >
              {l.label}
            </a>
          ))}
          <a
            href={bookingUrl(settings)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reserve-now-mobile-button"
            className="inline-flex rounded-full bg-ink text-sand text-sm px-5 py-2.5"
          >
            Reserve Now
          </a>
        </div>
      )}
    </header>
  );
};
